/**
 * Master transport for Sample Breaker.
 * Game speed and sample triggers both derive from BPM.
 */
export class BeatClock {
  /** Current tempo. */
  bpm: number;
  /** Beat units accumulated (fractional). */
  private beats = 0;
  /** Last whole beat index already dispatched. */
  private lastDispatchedBeat = -1;

  constructor(
    public startBpm = 100,
    public maxBpm = 180,
    /** BPM gained per second while the clock is ramping. */
    public rampPerSecond = 0.45,
  ) {
    this.bpm = startBpm;
  }

  get secondsPerBeat(): number {
    return 60 / Math.max(1, this.bpm);
  }

  /** 0..1 progress within the current beat. */
  get phase(): number {
    return this.beats - Math.floor(this.beats);
  }

  get beatIndex(): number {
    return Math.floor(this.beats);
  }

  /** Continuous total-beats value (fractional). Useful for sub-beat scheduling. */
  get totalBeats(): number {
    return this.beats;
  }

  /** True on downbeats (every 4). */
  get isDownbeat(): boolean {
    return this.beatIndex % 4 === 0;
  }

  /** Seconds until the next whole beat at the current BPM. */
  timeToNextBeat(): number {
    const frac = this.phase;
    const remaining = frac < 1e-6 ? 0 : 1 - frac;
    return remaining * this.secondsPerBeat;
  }

  /** How many whole beats will fire if we advance `delta` seconds. */
  peekBeatsIn(delta: number): number {
    const next = this.beats + delta / this.secondsPerBeat;
    return Math.max(0, Math.floor(next) - this.lastDispatchedBeat);
  }

  reset(startBpm = this.startBpm): void {
    this.startBpm = startBpm;
    this.bpm = startBpm;
    this.beats = 0;
    this.lastDispatchedBeat = -1;
  }

  /**
   * Advance the transport.
   * When `ramping` is true, BPM climbs toward maxBpm.
   * Returns beat indices that crossed this frame (usually 0 or 1).
   */
  update(delta: number, ramping: boolean): number[] {
    if (ramping && this.bpm < this.maxBpm) {
      this.bpm = Math.min(this.maxBpm, this.bpm + this.rampPerSecond * delta);
    }

    // Integrate with instantaneous tempo so ramps stay continuous.
    this.beats += delta / this.secondsPerBeat;

    const fired: number[] = [];
    while (this.lastDispatchedBeat < Math.floor(this.beats) - 1e-9) {
      this.lastDispatchedBeat += 1;
      fired.push(this.lastDispatchedBeat);
    }
    return fired;
  }

  /** Speed multiplier relative to the starting tempo. */
  speedScale(): number {
    return this.bpm / this.startBpm;
  }
}
